import { FirestoreDocumentReference, useUpflowyStore } from '@upflowy/firestore';
import { doc } from 'firebase/firestore';
import { useState } from 'react';

import { fetchDoc } from '../helpers/fetch-documents';

type CacheEntry<T> = {
  updated: Date;
  data: Promise<T>;
};

type Cache = Record<string, CacheEntry<unknown>>;

async function getCachedEntry<T>(
  setter: (id: string, value: CacheEntry<unknown>) => void,
  getter: (id: string) => CacheEntry<unknown>,
  ref: string,
  resolver: (r: string) => Promise<T | undefined>,
): Promise<T | undefined> {
  const cacheEntry = getter(ref);
  if (cacheEntry) return cacheEntry.data as Promise<T>;
  const newEntryPromise = resolver(ref);
  setter(ref, {
    updated: new Date(),
    data: newEntryPromise,
  });
  return newEntryPromise;
}

function useDocCache() {
  const firestore = useUpflowyStore();
  const [cache, setCache] = useState<Cache>({});

  const getEntry = (id: string) => cache[id];
  const setEntry = (id: string, value: CacheEntry<unknown>) => {
    setCache((c) => ({ ...c, [id]: value }));
  };

  return {
    async getDocument<T>(ref: FirestoreDocumentReference<T> | string) {
      const reference =
        typeof ref === 'string' ? (doc(firestore, ref) as FirestoreDocumentReference<T>) : ref;
      return getCachedEntry<T>(setEntry, getEntry, reference.path, async () => {
        const snapshot = await fetchDoc(reference);
        return snapshot.data();
      });
    },
  };
}

export default useDocCache;
